import { useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router";
import { Button } from "@mui/material";
import { enqueueSnackbar, type SnackbarKey } from "notistack";
import { APP_VERSION, fetchServerVersion } from "@/utils/appVersion";
import { SnackbarCloseAction } from "@/utils/notistack/snackbarActions";
import AppDialogConfirmation from "@/components/avulsos/appDialogConfirmation/AppDialogConfirmation";

const INTERVALO_VERIFICACAO_MS = 1 * 60_000;
const CHAVE_SNACKBAR_ATUALIZACAO = "app-update-available";

/** Ações do snackbar de atualização: "Atualizar agora" + fechar. */
function AppUpdateSnackbarAction({
	snackbarId,
	onAtualizarAgora,
}: {
	readonly snackbarId: SnackbarKey;
	readonly onAtualizarAgora: () => void;
}) {
	return (
		<>
			<Button color="inherit" size="small" onClick={onAtualizarAgora}>
				Atualizar agora
			</Button>
			{SnackbarCloseAction(snackbarId)}
		</>
	);
}

function notificarAtualizacao(onAtualizarAgora: () => void) {
	enqueueSnackbar("Uma nova versão do sistema está disponível. Salve seu trabalho e atualize.", {
		variant: "info",
		persist: true,
		preventDuplicate: true,
		key: CHAVE_SNACKBAR_ATUALIZACAO,
		action: (id) => <AppUpdateSnackbarAction snackbarId={id} onAtualizarAgora={onAtualizarAgora} />,
	});
}

/**
 * Observa proativamente se há uma versão nova publicada (deploy), comparando `APP_VERSION`
 * (embutida no bundle em execução) com `/version.json` do servidor. Ao detectar diferença,
 * mostra um snackbar persistente com "Atualizar agora".
 *
 * Nunca recarrega sozinho: o reload só acontece com confirmação do usuário — seja pelo botão
 * do snackbar, seja ao tentar trocar de tela. Com atualização pendente, `useBlocker` intercepta
 * a navegação e abre o modal antes de sair da tela, evitando que o `setupStaleChunkReload`
 * recarregue de forma abrupta e o usuário perca um formulário não salvo.
 */
export default function AppUpdateWatcher() {
	const avisadoRef = useRef(false);
	const [atualizacaoPendente, setAtualizacaoPendente] = useState(false);
	const [confirmacaoManualAberta, setConfirmacaoManualAberta] = useState(false);

	// Enquanto houver atualização pendente, bloqueia qualquer troca de rota para confirmar
	// com o usuário (que pode salvar antes) em vez de recarregar e perder dados não salvos.
	const blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			atualizacaoPendente && currentLocation.pathname !== nextLocation.pathname,
	);

	useEffect(() => {
		let cancelado = false;
		const controller = new AbortController();

		const verificarVersao = async () => {
			if (avisadoRef.current) return;

			const versaoServidor = await fetchServerVersion(controller.signal);
			if (cancelado || !versaoServidor) return;

			if (versaoServidor !== APP_VERSION) {
				avisadoRef.current = true;
				setAtualizacaoPendente(true);
				notificarAtualizacao(() => setConfirmacaoManualAberta(true));
			}
		};

		const verificarAoFocar = () => {
			if (document.visibilityState === "visible") verificarVersao();
		};

		verificarVersao();
		const intervalo = globalThis.setInterval(verificarVersao, INTERVALO_VERIFICACAO_MS);
		document.addEventListener("visibilitychange", verificarAoFocar);
		globalThis.addEventListener("focus", verificarAoFocar);

		return () => {
			cancelado = true;
			controller.abort();
			globalThis.clearInterval(intervalo);
			document.removeEventListener("visibilitychange", verificarAoFocar);
			globalThis.removeEventListener("focus", verificarAoFocar);
		};
	}, []);

	const bloqueado = blocker.state === "blocked";

	const confirmarAtualizacao = () => {
		if (bloqueado && blocker.location) {
			const { pathname, search, hash } = blocker.location;
			globalThis.location.assign(`${pathname}${search}${hash}`);
			return;
		}
		globalThis.location.reload();
	};

	const cancelarAtualizacao = () => {
		if (bloqueado) blocker.reset();
		setConfirmacaoManualAberta(false);
	};

	return (
		<AppDialogConfirmation
			open={bloqueado || confirmacaoManualAberta}
			variant="warning"
			title="Confirmar atualização"
			message="Uma nova versão do sistema está disponível. Para continuar, a página será recarregada e os dados não salvos nesta tela serão perdidos. Salve seu trabalho antes de prosseguir."
			confirmText="Atualizar"
			cancelText="Cancelar"
			onConfirm={confirmarAtualizacao}
			onCancel={cancelarAtualizacao}
		/>
	);
}
